import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaRobot, FaCogs, FaCodeBranch, FaBrain, FaChartLine, FaUsers, FaClock, FaEuroSign } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../../components/SEO';
import Spinner from '../../components/Spinner/Spinner';
import './Portfolio.css';

// Icon mapping for metrics
const metricIconMap = {
  chart: <FaChartLine />,
  users: <FaUsers />,
  clock: <FaClock />,
  euro: <FaEuroSign />,
  robot: <FaRobot />,
  cogs: <FaCogs />,
  brain: <FaBrain />
};

// Category icon mapping
const categoryIconMap = {
  rpa: <FaRobot />,
  robotics: <FaCogs />,
  lowcode: <FaCodeBranch />,
  ai: <FaBrain />
};

// Kept between visits: coming back to the page shows the projects at once, refreshed in the background
let cachedProjects = null;

const Portfolio = () => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [projects, setProjects] = useState(cachedProjects || []);
  const [loading, setLoading] = useState(!cachedProjects);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const categories = [
    { key: 'all', label: t('portfolio.categories.all'), icon: <FaChartLine /> },
    { key: 'rpa', label: t('portfolio.categories.rpa'), icon: <FaRobot /> },
    { key: 'robotics', label: t('portfolio.categories.robotics'), icon: <FaCogs /> },
    { key: 'lowcode', label: t('portfolio.categories.lowcode'), icon: <FaCodeBranch /> },
    { key: 'ai', label: t('portfolio.categories.ai'), icon: <FaBrain /> }
  ];

  // Default projects
  const defaultProjects = [
    {
      id: 'rossel',
      category: 'rpa',
      title_en: 'Process Automation - Media Group',
      title_fr: 'Automatisation des Processus - Groupe Média',
      description_en: 'End-to-end automation of HR and financial processes',
      description_fr: 'Automatisation des processus RH et financiers de bout en bout',
      results_en: '70% time reduction, 3 months to ROI',
      results_fr: 'Réduction de 70% du temps, ROI en 3 mois',
      technologies: ['UiPath', 'Power Automate', 'SAP Integration'],
      sector_en: 'Media',
      sector_fr: 'Média',
      image: '/assets/images/finance-reports.jpg',
      metrics: [
        { icon: 'clock', value: '70%', label_en: 'Time Reduction', label_fr: 'Réduction du temps' },
        { icon: 'euro', value: '3', label_en: 'Months for ROI', label_fr: 'Mois pour ROI' },
        { icon: 'users', value: '50+', label_en: 'Users', label_fr: 'Utilisateurs' }
      ]
    },
    {
      id: 'lvmh',
      category: 'ai',
      title_en: 'AI Automation - Luxury Group',
      title_fr: 'Automatisation IA - Groupe de Luxe',
      description_en: 'AI-powered document processing and intelligent automation',
      description_fr: 'Traitement intelligent de documents avec IA',
      results_en: '85% efficiency, 200+ employees impacted',
      results_fr: '85% d\'efficacité, 200+ employés impactés',
      technologies: ['Azure AI', 'UiPath', 'Document Understanding'],
      sector_en: 'Luxury',
      sector_fr: 'Luxe',
      image: '/assets/images/warehouse-logistics.jpg',
      metrics: [
        { icon: 'chart', value: '85%', label_en: 'Efficiency', label_fr: 'Efficacité' },
        { icon: 'users', value: '200+', label_en: 'Employees', label_fr: 'Employés' },
        { icon: 'robot', value: '15', label_en: 'Processes', label_fr: 'Processus' }
      ]
    },
    {
      id: 'rocher',
      category: 'lowcode',
      title_en: 'Digital Transformation - Cosmetics Group',
      title_fr: 'Transformation Digitale - Groupe Cosmétique',
      description_en: 'Low-code platform for supply chain automation',
      description_fr: 'Plateforme low-code pour automatisation supply chain',
      results_en: '2M€ annual savings, 90% automation',
      results_fr: '2M€ économies/an, 90% automatisation',
      technologies: ['Power Platform', 'Power Automate', 'Dataverse'],
      sector_en: 'Retail',
      sector_fr: 'Retail',
      image: '/assets/images/ecommerce-shopping.jpg',
      metrics: [
        { icon: 'euro', value: '2M€', label_en: 'Savings/year', label_fr: 'Économies/an' },
        { icon: 'chart', value: '90%', label_en: 'Automation', label_fr: 'Automatisation' },
        { icon: 'users', value: '500+', label_en: 'Users', label_fr: 'Utilisateurs' }
      ]
    },
    {
      id: 'manufacturing',
      category: 'robotics',
      title_en: 'Industrial Automation - Manufacturing Group',
      title_fr: 'Automatisation Industrielle - Groupe Manufacturier',
      description_en: 'Collaborative robots for assembly and quality control',
      description_fr: 'Robots collaboratifs pour assemblage et contrôle qualité',
      results_en: '120% productivity increase, 95% defect reduction',
      results_fr: 'Augmentation de 120% de la productivité, réduction de 95% des défauts',
      technologies: ['ABB Robotics', 'Computer Vision', 'IoT Sensors', 'Predictive Analytics'],
      sector_en: 'Industry',
      sector_fr: 'Industrie',
      image: '/assets/images/manufacturing.jpg',
      metrics: [
        { icon: 'chart', value: '120%', label_en: 'Productivity', label_fr: 'Productivité' },
        { icon: 'cogs', value: '12', label_en: 'Robots', label_fr: 'Robots' },
        { icon: 'clock', value: '24/7', label_en: 'Operation', label_fr: 'Opération' }
      ]
    },
    {
      id: 'bank',
      category: 'rpa',
      title_en: 'Digital Transformation - European Bank',
      title_fr: 'Transformation Digitale - Banque Européenne',
      description_en: 'Compliance and risk management process automation',
      description_fr: 'Automatisation des processus de conformité et gestion des risques',
      results_en: '10,000 files/day processed, 100% compliance',
      results_fr: 'Traitement de 10,000 dossiers/jour, conformité 100% respectée',
      technologies: ['UiPath', 'Blue Prism', 'Machine Learning', 'OCR'],
      sector_en: 'Banking',
      sector_fr: 'Banque',
      image: '/assets/images/data-analytics.jpg',
      metrics: [
        { icon: 'chart', value: '10K', label_en: 'Files/day', label_fr: 'Dossiers/jour' },
        { icon: 'robot', value: '25', label_en: 'Bots', label_fr: 'Bots' },
        { icon: 'clock', value: '80%', label_en: 'Time saved', label_fr: 'Temps économisé' }
      ]
    },
    {
      id: 'healthcare',
      category: 'ai',
      title_en: 'Predictive AI - Hospital Group',
      title_fr: 'IA Prédictive - Groupe Hospitalier',
      description_en: 'AI system for resource planning and admission prediction',
      description_fr: 'Système d\'IA pour la planification des ressources et prédiction des admissions',
      results_en: '60% schedule optimization, 40% wait time reduction',
      results_fr: 'Optimisation de 60% des plannings, réduction de 40% des temps d\'attente',
      technologies: ['TensorFlow', 'Azure AI', 'Power BI', 'Python'],
      sector_en: 'Healthcare',
      sector_fr: 'Santé',
      image: '/assets/images/ai-technology.jpg',
      metrics: [
        { icon: 'users', value: '60%', label_en: 'Schedule optimized', label_fr: 'Planning optimisé' },
        { icon: 'clock', value: '40%', label_en: 'Wait time', label_fr: 'Temps d\'attente' },
        { icon: 'brain', value: '95%', label_en: 'AI Accuracy', label_fr: 'Précision IA' }
      ]
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Projects
        const projectsQuery = query(collection(db, 'portfolio'), orderBy('order', 'asc'));
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectsData = projectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        cachedProjects = projectsData.length > 0 ? projectsData : defaultProjects;
        setProjects(cachedProjects);
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        if (!cachedProjects) setProjects(defaultProjects);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to get localized content
  const getLocalizedContent = (item, field) => {
    const langField = `${field}_${currentLang}`;
    return item[langField] || item[`${field}_en`] || item[field] || '';
  };

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  const isFr = currentLang === 'fr';

  // Rendered in the loading state too, so the prerendered page always has its meta tags
  const seo = (
    <SEO
      title={isFr ? 'Portfolio - Nos Réalisations' : 'Portfolio - Our Achievements'}
      description={isFr
        ? 'Découvrez nos projets d\'automatisation RPA réussis : Groupe Rossel, LVMH, Groupe Rocher et plus encore. Cas clients et résultats concrets.'
        : 'Discover our successful RPA automation projects: Groupe Rossel, LVMH, Groupe Rocher and more. Client cases and concrete results.'
      }
      canonical="/portfolio"
    />
  );

  if (loading) {
    return (
      <div className="portfolio">
        {seo}
        <section className="portfolio-hero">
          <div className="hero-background">
            <img
              src="/assets/images/dashboard-analytics.jpg"
              alt="Portfolio"
            />
            <div className="overlay"></div>
          </div>
          <div className="container">
            <motion.div
              className="portfolio-hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>{t('portfolio.title')}</h1>
              <p>{t('portfolio.subtitle')}</p>
            </motion.div>
          </div>
        </section>
        <section className="portfolio-content">
          <div className="container">
            <Spinner size="medium" text={currentLang === 'fr' ? 'Chargement des projets...' : 'Loading projects...'} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="portfolio">
      {seo}
      {/* Hero Section */}
      <section className="portfolio-hero">
        <div className="hero-background">
          <img
            src="/assets/images/dashboard-analytics.jpg"
            alt="Portfolio"
          />
          <div className="overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="portfolio-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{t('portfolio.title')}</h1>
            <p>{t('portfolio.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="portfolio-content" ref={ref}>
        <div className="container">
          {/* Category Filter */}
          <motion.div
            className="category-filter"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {categories.map((category) => (
              <button
                key={category.key}
                className={`filter-btn ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                {category.icon}
                <span>{category.label}</span>
              </button>
            ))}
          </motion.div>

          {/* Projects Grid */}
          <div className="projects-grid">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-card"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="project-badges">
                  <span className="project-category-badge">
                    {categoryIconMap[project.category] || <FaRobot />}
                    {categories.find(cat => cat.key === project.category)?.label}
                  </span>
                  <span className="project-sector">{getLocalizedContent(project, 'sector')}</span>
                </div>

                <h2 className="project-title">{getLocalizedContent(project, 'title')}</h2>

                <p className="project-description">{getLocalizedContent(project, 'description')}</p>

                <div className="project-technologies">
                  {(project.technologies || []).map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                </div>

                <div className="project-metrics">
                  {(project.metrics || []).map((metric, idx) => (
                    <div key={idx} className="metric">
                      <div className="metric-icon">
                        {metric.iconUrl ? (
                          <img src={metric.iconUrl} alt="" className="metric-icon-img" />
                        ) : (
                          metricIconMap[metric.icon] || <FaChartLine />
                        )}
                      </div>
                      <div className="metric-value">{metric.value}</div>
                      <div className="metric-label">{getLocalizedContent(metric, 'label')}</div>
                    </div>
                  ))}
                </div>

                <div className="project-results">
                  <h3>{currentLang === 'fr' ? 'Résultats obtenus' : 'Results achieved'}</h3>
                  <ul className="results-list">
                    {getLocalizedContent(project, 'results')
                      .split(/[,.]/)
                      .map(r => r.trim())
                      .filter(r => r.length > 0)
                      .map((result, idx) => (
                        <li key={idx}>{result}</li>
                      ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
